import { CommandResult, Commands } from "@highmarch/domain";
import type { Cell } from "@highmarch/domain";
import { audio } from "../audio/audio";
import { toast } from "../ui/toast";
import { HINT_MS, LevelParts } from "./levelParts";
import { rallyCells, rallyUsable } from "./rally";

/**
 * The troops' rally flag (QoL C5): the HUD flag button arms it — the button lights, a prompt asks for the road, the
 * road cells glow — and the next map tap plants it; pressed again, it disarms. The long-press on the map still plants
 * it directly; nobody found that unaided, and the button used to do nothing at all (owner report 2026-09-24).
 */
export class LevelRally {
  /** How long the "not now" toast for the flag button stays up. */
  static readonly RallyToastMs = 2200;
  private isArmed = false;

  constructor(private readonly p: LevelParts) {}

  /** The flag is armed: the next map tap plants it. */
  get armed(): boolean { return this.isArmed; }

  /** The HUD flag button. */
  toggle(): void {
    if (this.isArmed) { this.setArmed(false); return; }
    if (!this.usable()) { toast(this.p.uiRoot, "The flag rallies troops mid-wave. Try it when the horde arrives.", LevelRally.RallyToastMs); return; }
    this.setArmed(true);
  }

  private usable(): boolean { return !this.p.ended && !this.p.disposed && rallyUsable(this.p.sim, this.p.towerInfo.isOpen); }

  /** After the sim moved: an offer, the tower sheet, the wave's end or the level's end takes the map back from the flag. */
  settle(): void { if (this.isArmed && !this.usable()) this.setArmed(false); }

  private setArmed(on: boolean): void {
    if (on === this.isArmed) return;
    this.isArmed = on;
    this.p.hud.setRallyArmed(on);
    if (on) this.p.raise("rallyArmed");
    if (on) this.p.scene.placement.showRallyCells(this.cells());
    else this.p.scene.placement.clear();
  }

  /** The road cells the flag can stand on. */
  cells(): Cell[] { return rallyCells(this.p.sim); }

  /** A map tap with the flag armed: the road cell drawn nearest the finger gets the flag; a tap off the road cancels. */
  onTap(x: number, y: number): void {
    const cell = this.p.picker.nearestOnScreen(x, y, this.cells(), (c) => c);
    if (cell === null) { this.setArmed(false); this.p.hud.rallyNote("Rally cancelled", HINT_MS); return; }
    this.plant(cell);
  }

  /** Moves the rally flag through the command path (deterministic: the sim re-routes every troop on its tick). */
  plant(cell: Cell): void {
    this.setArmed(false);
    if (this.p.execute(Commands.setRally(cell.x, cell.y)) !== CommandResult.Ok) { this.p.hud.rallyNote("Troops can't stand there", HINT_MS); return; }
    audio.play("ui");
    this.p.raise("rallyPlanted");
    const sim = this.p.sim;
    const note = cell.equals(sim.grid.base) ? "Troops fall back to the fortress" : sim.troops.aliveCount > 0 ? "Troops rallying to the flag" : "Flag planted: new troops muster there";
    this.p.hud.rallyNote(note, HINT_MS);
  }
}
